import {
  AccountInfo,
  Box,
  Col,
  Divider,
  Flex,
  Image,
  MoreButton,
  Row,
  Space,
  Text,
} from '@/components';
import { UploadedAttachment } from '@/features/attachment/_components/UploadedAttachment';
import type { AttachmentsModel } from '@/features/attachment/_models/attachment';
import type { UserModel } from '@/features/tenant-manager/user/_models/user';
import { ITEM_SPLITTER } from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { CommentReplyForm } from './CommentReplyForm';

type Props = {
  content: string;
  createdAt: string;
  attachments: z.infer<typeof AttachmentsModel>;
  id: string;
  onDelete?: (id: string) => void;
  onUpdate?: (id: string, value: { content: string }) => void;
  editable?: boolean;
  replier: z.infer<typeof UserModel> | undefined;
};

export const CommentReply: FC<Props> = ({
  content,
  createdAt,
  attachments,
  id,
  onDelete,
  onUpdate,
  editable,
  replier,
}) => {
  const { t } = useTranslation();
  const [showEdit, setShowEdit] = useState(false);

  const onSelectDropdown = (key: string) => {
    switch (key) {
      case 'delete':
        onDelete?.(id);
        break;
      case 'edit':
        setShowEdit((prev) => !prev);
        break;
      default:
        break;
    }
  };

  return (
    <Row gutter={[0, 4]}>
      <Col span={24}>
        <Flex justify="space-between" align="flex-start" gap={8}>
          {replier && (
            <AccountInfo
              iconUrl={replier.iconDataUrl}
              accountName={replier.name}
              organizationName={replier.organizations
                ?.map((d) => d.name)
                .join(ITEM_SPLITTER)}
            />
          )}
          <Space size={0}>
            <Text
              variant="footnote"
              color="tertiary"
              text={createdAt}
              ellipsis
            />
            {editable && (
              <>
                <Divider type="vertical" />
                <MoreButton
                  size="small"
                  items={[
                    ...(!showEdit
                      ? [
                          {
                            key: 'edit',
                            label: t('edit'),
                          },
                        ]
                      : []),
                    { key: 'delete', label: t('delete'), danger: true },
                  ]}
                  onClick={({ key }) => {
                    onSelectDropdown(key);
                  }}
                  isBorder={false}
                />
              </>
            )}
          </Space>
        </Flex>
      </Col>
      <Col span={24}>
        <Flex vertical style={{ paddingLeft: 36 }}>
          {showEdit ? (
            <CommentReplyForm
              type="edit"
              initialValues={{
                content,
                fileEntries: attachments.flatMap((attachment) =>
                  attachment.url
                    ? [
                        {
                          file: null,
                          url: attachment.url,
                          attachment,
                        },
                      ]
                    : [],
                ),
              }}
              attachments={attachments}
              onSubmit={(values) => {
                onUpdate?.(id, values);
                setShowEdit(false);
              }}
              onCancel={() => {
                setShowEdit(false);
              }}
            />
          ) : (
            <>
              <Text weight="regular" text={content} />
              <Box>
                <Image.PreviewGroup>
                  {attachments?.map((attachment) => (
                    <UploadedAttachment
                      key={attachment.id}
                      attachment={attachment}
                    />
                  ))}
                </Image.PreviewGroup>
              </Box>
            </>
          )}
        </Flex>
      </Col>
    </Row>
  );
};
